import React, { useState, useEffect } from 'react';
import { useApi } from '../api';
import { formatearPrecio } from './utils';
import {
  Paper, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Box, TextField,
  Select, MenuItem, FormControl, InputLabel, IconButton
} from '@mui/material';
import EngineeringIcon from '@mui/icons-material/Engineering';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import GroupIcon from '@mui/icons-material/Group';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { motion } from 'framer-motion';

function Tecnicos() {
  const [tecnicos, setTecnicos] = useState([]);
  const [nombreNuevo, setNombreNuevo] = useState('');
  const [tecnicoSeleccionado, setTecnicoSeleccionado] = useState('');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [reparaciones, setReparaciones] = useState([]);
  const totalReparaciones = reparaciones.reduce((acc, r) => acc + parseFloat(r.costo), 0);
  const api = useApi();

  const fetchTecnicos = async () => {
    try {
      const response = await api.get(`${process.env.REACT_APP_API_URL}/api/tecnicos/`);
      setTecnicos(response.data);
    } catch (error) {
      console.error('Error cargando técnicos:', error);
    }
  };

  useEffect(() => { fetchTecnicos(); }, []);

  const agregarTecnico = async () => {
    if (!nombreNuevo.trim()) return alert('Ingrese un nombre válido.');
    try {
      await api.post(`${process.env.REACT_APP_API_URL}/api/tecnicos/`, { nombre: nombreNuevo });
      setNombreNuevo('');
      fetchTecnicos();
    } catch (error) {
      console.error('Error agregando técnico:', error);
    }
  };

  const eliminarTecnico = async (id) => {
    if (!window.confirm('¿Seguro que deseas eliminar este técnico?')) return;
    try {
      await api.delete(`${process.env.REACT_APP_API_URL}/api/tecnicos/${id}/`);
      fetchTecnicos();
    } catch (error) {
      console.error('Error eliminando técnico:', error);
    }
  };

  const buscarReparaciones = () => {
    if (!tecnicoSeleccionado || !fechaInicio || !fechaFin) {
      alert('Selecciona técnico y fechas');
      return;
    }
    api.get(`${process.env.REACT_APP_API_URL}/api/reparaciones/`, {
      params: { tecnico_id: tecnicoSeleccionado, fecha_inicio: fechaInicio, fecha_fin: fechaFin }
    })
      .then(response => setReparaciones(response.data))
      .catch(error => {
        console.error('Error buscando reparaciones:', error);
        setReparaciones([]);
      });
  };

  return (
    <Paper sx={{ padding: 2 }}>
      {/* Título principal animado */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.6, 0.01, 0.05, 0.95] }}
      >
        <Typography className="titulo-app" variant="h5" gutterBottom>
          <EngineeringIcon sx={{ mr: 1, fontSize: 32, color: '#083a94', verticalAlign: 'middle' }} />
          Gestión de Técnicos
        </Typography>
      </motion.div>

      {/* Sección: Agregar técnico */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.15, ease: [0.6, 0.01, 0.05, 0.95] }}
      >
        <Paper elevation={2} sx={{ p: 2, mb: 3, borderLeft: '4px solid #083a94', borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ color: '#083a94', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonAddIcon sx={{ fontSize: 24 }} />
            Agregar Técnico
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              label="Nombre del técnico"
              value={nombreNuevo}
              onChange={e => setNombreNuevo(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && agregarTecnico()}
              sx={{ flex: 1 }}
            />
            <Button variant="contained" color="success" startIcon={<PersonAddIcon />} onClick={agregarTecnico}>
              Agregar
            </Button>
          </Box>
        </Paper>
      </motion.div>

      {/* Sección: Lista de técnicos */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.25, ease: [0.6, 0.01, 0.05, 0.95] }}
      >
        <Paper elevation={2} sx={{ p: 2, mb: 3, borderLeft: '4px solid #3b82f6', borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ color: '#1d4ed8', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
            <GroupIcon sx={{ fontSize: 24 }} />
            Técnicos Registrados
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: '#083a94' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Nombre</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', width: 120 }}>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tecnicos.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2} align="center">No hay técnicos registrados.</TableCell>
                  </TableRow>
                ) : tecnicos.map(t => (
                  <TableRow key={t.id} hover>
                    <TableCell>{t.nombre}</TableCell>
                    <TableCell>
                      <IconButton size="small" color="error" onClick={() => eliminarTecnico(t.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </motion.div>

      {/* Sección: Buscar reparaciones por técnico */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.35, ease: [0.6, 0.01, 0.05, 0.95] }}
      >
        <Paper elevation={2} sx={{ p: 2, mb: 3, borderLeft: '4px solid #10b981', borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ color: '#065f46', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
            <ManageSearchIcon sx={{ fontSize: 24 }} />
            Buscar Reparaciones por Técnico
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 3 }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Técnico</InputLabel>
              <Select
                value={tecnicoSeleccionado}
                label="Técnico"
                onChange={e => setTecnicoSeleccionado(e.target.value)}
              >
                <MenuItem value="">Seleccionar...</MenuItem>
                {tecnicos.map(t => (
                  <MenuItem key={t.id} value={t.id}>{t.nombre}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Fecha inicio"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={fechaInicio}
              onChange={e => setFechaInicio(e.target.value)}
            />
            <TextField
              size="small"
              label="Fecha fin"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={fechaFin}
              onChange={e => setFechaFin(e.target.value)}
            />
            <Button variant="contained" startIcon={<SearchIcon />} onClick={buscarReparaciones}>
              Buscar
            </Button>
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: '#083a94' }}>
                  {['Fecha', 'Descripción', 'Orden', 'Costo'].map(h => (
                    <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {reparaciones.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">No hay resultados. Selecciona un técnico y fechas para buscar.</TableCell>
                  </TableRow>
                ) : (
                  <>
                    {reparaciones.map(r => (
                      <TableRow key={r.id} hover>
                        <TableCell>{r.fecha}</TableCell>
                        <TableCell>{r.descripcion}</TableCell>
                        <TableCell>{r.orden}</TableCell>
                        <TableCell>${formatearPrecio(r.costo)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ backgroundColor: '#f1f5f9' }}>
                      <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold' }}>
                        Total ({reparaciones.length} reparaciones)
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>${formatearPrecio(totalReparaciones)}</TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </motion.div>
    </Paper>
  );
}

export default Tecnicos;
